/**
 * editor2/hooks/useGenericEdit.ts
 * GenericEditDialog の状態管理フック
 * ReservationEditorPanel の handleOpenEdit / handleOpenEditMulti / handleSaveGenericEdit を抽出
 */
import { useState, useCallback } from 'react';
import { isoToLocalInput, localInputToIso } from '../../../lib/dateUtils';
import type { GenericEditConfig, MultiFieldConfig } from '../types';

interface UseGenericEditOptions {
  getData: () => Record<string, any>;
  onChange?: (updates: Record<string, any>) => void;
  statusOptions?: any[];
}

export default function useGenericEdit({ getData, onChange, statusOptions }: UseGenericEditOptions) {
  const [genericEditConfig, setGenericEditConfig] = useState<GenericEditConfig | null>(null);

  const handleOpenEdit = useCallback(
    (field: string, title: string, type: string = 'text', options: any[] = []) => {
      const data = getData();
      let val: any = data[field] ?? '';

      if (type === 'datetime-local') {
        val = isoToLocalInput(val);
      } else if (field === 'status' && type === 'select' && statusOptions) {
        // ステータスコードをIDに解決
        const match = statusOptions.find(
          (s: any) => String(s.id) === String(val) || s.code === val,
        );
        if (match) val = match.id;
      }

      setGenericEditConfig({ field, title, type, value: val, options, multiFields: null });
    },
    [getData, statusOptions],
  );

  const handleOpenEditMulti = useCallback(
    (title: string, multiFields: MultiFieldConfig[]) => {
      const data = getData();
      const valueObj: Record<string, any> = {};
      multiFields.forEach((f) => {
        let v: any = data[f.field] ?? '';
        if (f.type === 'datetime-local') v = isoToLocalInput(v);
        valueObj[f.field] = v;
      });
      setGenericEditConfig({ title, type: 'multi', value: valueObj, multiFields });
    },
    [getData],
  );

  const handleSaveGenericEdit = useCallback(() => {
    if (!onChange || !genericEditConfig) {
      setGenericEditConfig(null);
      return;
    }

    if (genericEditConfig.type === 'multi' && genericEditConfig.multiFields) {
      const updates: Record<string, any> = {};
      genericEditConfig.multiFields.forEach((f) => {
        let savedVal = genericEditConfig.value[f.field];
        if (f.type === 'number') savedVal = savedVal === '' ? null : Number(savedVal);
        else if (f.type === 'datetime-local') savedVal = localInputToIso(savedVal);
        updates[f.field] = savedVal;
      });
      onChange(updates);
    } else {
      let savedVal = genericEditConfig.value;
      if (genericEditConfig.type === 'number') savedVal = savedVal === '' ? null : Number(savedVal);
      else if (genericEditConfig.type === 'datetime-local') savedVal = localInputToIso(savedVal);
      onChange({ [genericEditConfig.field!]: savedVal });
    }

    setGenericEditConfig(null);
  }, [genericEditConfig, onChange]);

  const handleCancelGenericEdit = useCallback(() => {
    setGenericEditConfig(null);
  }, []);

  const setGenericEditValue = useCallback((value: any) => {
    setGenericEditConfig((prev) => (prev ? { ...prev, value } : prev));
  }, []);

  return {
    genericEditConfig,
    setGenericEditConfig,
    setGenericEditValue,
    handleOpenEdit,
    handleOpenEditMulti,
    handleSaveGenericEdit,
    handleCancelGenericEdit,
  };
}
